import * as Arr from 'effect/Array';
import * as Data from 'effect/Data';
import * as Either from 'effect/Either';
import * as Fn from 'effect/Function';
import * as P from 'effect/Predicate';
import * as Str from 'effect/String';
import * as Utils from '../internal.utils';

const SORT_CODE_LENGTH = 6;

const hasValidLength: P.Predicate<string> = (s) =>
  Str.length(s) === SORT_CODE_LENGTH;

/**
 * A sort code is a six-digit number used to identify a specific bank and branch in the UK.
 */
export interface SortCode {
  readonly _tag: 'SortCode';
  /** Sort code in electronic format (without separators) */
  readonly sortCode: string;
}
export const SortCode = Data.tagged<SortCode>('SortCode');

export function fromString(
  sortCodeStr: string,
): Either.Either<SortCode, string> {
  return Fn.pipe(
    sortCodeStr,
    Str.replace(/[-]/g, ''),
    Either.liftPredicate(
      hasValidLength,
      () => `Sort code must be ${SORT_CODE_LENGTH} characters long`,
    ),
    Either.map((sortCode) => SortCode({ sortCode })),
  );
}

export const printFormat =
  (separator = '-') =>
  (code: SortCode): string =>
    Fn.pipe(code.sortCode, Utils.getGrouped(2), Arr.join(separator));

/**
 * Represents SortCodes
 *
 * @deprecated use {@link SortCode} instead.
 */
export class LegacySortCode {
  /**
   * SortCode in electronic format (without separators)
   */
  private _value: string;

  constructor(sortCode: string) {
    this._value = sortCode;
  }

  public static parse(sortCodeStr: string): LegacySortCode {
    // Remove separators
    return new LegacySortCode(sortCodeStr.replace(/[-]/g, ''));
  }

  public get value(): string {
    return this._value;
  }

  /**
   * @param separator Separator between groups, defaults to '-'
   *
   * @returns
   * SortCode as string, grouped in groups of 2
   */
  public format(separator: string = '-'): string {
    return Fn.pipe(this.value, Utils.getGrouped(2), Arr.join(separator));
  }
}
